/* eslint-disable react-refresh/only-export-components */
import { toast as sonnerToast, type ExternalToast } from "sonner";
import {
  CheckCircle2,
  AlertCircle,
  Info,
  X,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/utils/cn";

interface CustomToastProps {
  variant?: "success" | "error" | "info" | "warning" | "default" | "loading";
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  t: string | number;
  className?: string;
  wrapperClassName?: string;
}

/**
 * CustomToast 컴포넌트 - sonner의 custom toast로 사용됩니다.
 * 컴파운드 패턴을 지원하며, 타이틀과 설명을 포함할 수 있습니다.
 */
export const CustomToast = ({
  variant = "default",
  title,
  description,
  className,
  wrapperClassName,
  children,
  t,
}: CustomToastProps) => {
  const getIcon = () => {
    const iconClass =
      "w-5 h-5 transition-all duration-500 animate-in fade-in zoom-in";

    switch (variant) {
      case "success":
        return <CheckCircle2 className={cn(iconClass, "text-white!")} />;
      case "error":
        return <AlertCircle className={cn(iconClass, "text-white!")} />;
      case "info":
        return <Info className={cn(iconClass, "text-blue-500 ")} />;
      case "warning":
        return <AlertTriangle className={cn(iconClass, "text-amber-500")} />;
      case "loading":
        return (
          <Loader2 className={cn(iconClass, "text-blue-500 animate-spin")} />
        );
      default:
        return null;
    }
  };

  const getVariantStyles = () => {
    switch (variant) {
      case "success":
        return "bg-teal-500! text-white! border-transparent";
      case "error":
        return "bg-red-400! text-white! border-transparent";
      case "info":
        return "bg-white text-gray-900 border-blue-100";
      case "warning":
        return "bg-white text-gray-900 border-amber-100";
      case "loading":
        return "bg-white text-gray-900 border-blue-50";
      default:
        return "bg-white text-gray-900 border-gray-100";
    }
  };

  return (
    <div
      className={cn(
        "relative overflow-hidden flex items-start gap-3 font-pretendard p-4 rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.1)] w-[350px]",
        "transition-all duration-500 ease-in-out border-0!",
        variant === "success" || variant === "error"
          ? "text-white!"
          : "text-gray-900",
        wrapperClassName
      )}
    >
      {/* 배경 레이어: 그라데이션 전환을 자연스럽게 하기 위해 별도 분리 */}
      <div
        className={cn(
          "absolute inset-0 z-0 transition-all duration-700 ease-in-out",
          getVariantStyles(),
          className
        )}
      />

      <div className="relative z-10 flex-shrink-0 pt-0.5 transition-all duration-500">
        {getIcon()}
      </div>
      <div className="relative z-10 flex-grow flex flex-col gap-1 min-w-0 transition-colors duration-500">
        {children ? (
          children
        ) : (
          <>
            {title && (
              <div className="text-sm font-medium break-words transition-colors duration-500">
                {title}
              </div>
            )}
            {description && (
              <div
                className={cn(
                  "text-xs break-words transition-colors duration-500",
                  variant === "success" || variant === "error"
                    ? "text-white/90"
                    : "text-gray-500"
                )}
              >
                {description}
              </div>
            )}
          </>
        )}
      </div>

      {/* 로딩 중일 때는 닫기 버튼을 숨김 */}
      {variant !== "loading" && (
        <button
          onClick={() => sonnerToast.dismiss(t)}
          className="relative z-10 flex-shrink-0 transition-colors p-1"
        >
          <X
            className={cn(
              "size-4! transition-all duration-500",
              variant === "success" || variant === "error"
                ? "text-zinc-50/70! hover:text-zinc-50! "
                : "text-gray-400 hover:text-gray-600"
            )}
          />
        </button>
      )}
    </div>
  );
};

// Compound components
CustomToast.Title = ({ children }: { children: ReactNode }) => (
  <div className="text-sm font-semibold text-gray-900 break-words">{children}</div>
);

CustomToast.Description = ({ children }: { children: ReactNode }) => (
  <div className="text-xs text-gray-500 break-words">{children}</div>
);

export const toast = (
  message: ReactNode,
  data?: ExternalToast & { description?: ReactNode },
  className?: string
) => {
  return sonnerToast.custom(
    (t) => (
      <CustomToast
        t={t}
        variant="default"
        title={message}
        description={data?.description}
        className={className}
      />
    ),
    data
  );
};

toast.success = (
  message: ReactNode,
  data?: ExternalToast & { description?: ReactNode },
  className?: string
) => {
  return sonnerToast.custom(
    (t) => (
      <CustomToast
        t={t}
        variant="success"
        title={message}
        description={data?.description}
        className={className ?? data?.className}
      />
    ),
    { ...data, description: null, className: undefined }
  );
};

toast.error = (
  message: ReactNode,
  data?: ExternalToast & { description?: ReactNode },
  className?: string
) => {
  return sonnerToast.custom(
    (t) => (
      <CustomToast
        t={t}
        variant="error"
        title={message}
        description={data?.description}
        className={className}
      />
    ),
    { ...data, description: null, className: undefined }
  );
};

toast.info = (
  message: ReactNode,
  data?: ExternalToast & { description?: ReactNode },
  className?: string
) => {
  return sonnerToast.custom(
    (t) => (
      <CustomToast
        t={t}
        variant="info"
        title={message}
        description={data?.description}
        className={className}
      />
    ),
    data
  );
};

toast.warning = (
  message: ReactNode,
  data?: ExternalToast & { description?: ReactNode; wrapperClassName?: string; },
  className?: string
) => {
  return sonnerToast.custom(
    (t) => (
      <CustomToast
        t={t}
        variant="warning"
        title={message}
        description={data?.description}
        wrapperClassName={data?.wrapperClassName}
        className={className}
      />
    ),
    data
  );
};

toast.promise = <T,>(
  promise: Promise<T> | (() => Promise<T>),
  data: {
    loading: ReactNode;
    success: (data: T) => ReactNode;
    error: (error: unknown) => ReactNode;
    description?: ReactNode;
    loadingDescription?: ReactNode;
    successDescription?: (data: T) => ReactNode;
    errorDescription?: (error: unknown) => ReactNode;
    successClassName?: string;
    errorClassName?: string;
    loadingClassName?: string;
  } & ExternalToast
) => {
  // 1. 로딩 상태 시작 (커스텀 스타일)
  const id = sonnerToast.custom(
    (t) => (
      <CustomToast
        t={t}
        variant="loading"
        title={data.loading}
        description={data.loadingDescription || data.description}
        className={data.loadingClassName}
      />
    ),
    { ...data, description: null, duration: Infinity } // 로딩 중에는 자동으로 닫히지 않음
  );

  const p = typeof promise === "function" ? promise() : promise;

  p.then((res) => {
    // 2. 성공 시 성공 스타일로 교체
    sonnerToast.custom(
      (t) => (
        <CustomToast
          t={t}
          variant="success"
          title={data.success(res)}
          description={
            data.successDescription
              ? data.successDescription(res)
              : data.description
          }
          className={data.successClassName}
        />
      ),
      { ...data, description: null, id }
    );
  }).catch((err) => {
    // 3. 실패 시 에러 스타일로 교체
    sonnerToast.custom(
      (t) => (
        <CustomToast
          t={t}
          variant="error"
          title={data.error(err)}
          description={
            data.errorDescription
              ? data.errorDescription(err)
              : data.description
          }
          className={data.errorClassName}
        />
      ),
      { ...data, description: null, id }
    );
  });

  return id;
};

// sonner의 기본 기능도 노출
toast.dismiss = sonnerToast.dismiss;
toast.loading = (
  message: ReactNode,
  data?: ExternalToast & { description?: ReactNode },
  className?: string
) => {
  return sonnerToast.custom(
    (t) => (
      <CustomToast
        t={t}
        variant="loading"
        title={message}
        description={data?.description}
        className={className}
      />
    ),
    { ...data, description: null, duration: Infinity } // description: null로 sonner 기본 렌더링 방지
  );
};
toast.custom = sonnerToast.custom;

export default CustomToast;
